import { gql } from '@apollo/client';

export const MEETING_PACKAGE_QUEUE_QUERY = gql`
  query MeetingPackageQueue(
    $status: MeetingPackageVenueStatus
    $includeAll: Boolean
    $includeInactive: Boolean
    $runId: Int
    $autoCreateOnly: Boolean
    $matchOutcome: MeetingPackageMatchOutcome
    $search: String
    $skip: Int
    $take: Int
  ) {
    meetingPackageQueue(
      status: $status
      includeAll: $includeAll
      includeInactive: $includeInactive
      runId: $runId
      autoCreateOnly: $autoCreateOnly
      matchOutcome: $matchOutcome
      search: $search
      skip: $skip
      take: $take
    ) {
      totalCount
      venues {
        id
        externalVenueId
        venueName
        resolution
        status
        hotelId
        payloadHash
        matchScore
        runnerUpScore
        evidenceCoverage
        matchMethod
        matchExplanationJson
        lastSeenAt
        inactiveAt
        retryCount
        lastError
      }
    }
  }
`;

export const MEETING_PACKAGE_QUEUE_COUNTS_QUERY = gql`
  query MeetingPackageQueueCounts($search: String, $runId: Int, $includeInactive: Boolean) {
    meetingPackageQueueCounts(search: $search, runId: $runId, includeInactive: $includeInactive) {
      total
      matched
      newHotel
      needsReview
      incomplete
      inactive
      lastRunStartedAt
      lastSuccessfulRunStartedAt
    }
  }
`;

export const MEETING_PACKAGE_INACTIVE_COUNT_QUERY = gql`
  query MeetingPackageInactiveCount($runId: Int, $matchOutcome: MeetingPackageMatchOutcome, $search: String) {
    meetingPackageQueue(
      status: INACTIVE
      includeAll: true
      includeInactive: true
      runId: $runId
      matchOutcome: $matchOutcome
      search: $search
      take: 1
    ) {
      totalCount
    }
  }
`;

export const MEETING_PACKAGE_VENUE_QUERY = gql`
  query MeetingPackageVenue($externalVenueId: String!) {
    meetingPackageVenue(externalVenueId: $externalVenueId) {
      id
      externalVenueId
      venueName
      resolution
      status
      hotelId
      payloadHash
      payloadJson
      topCandidatesJson
      matchExplanationJson
      matchScore
      runnerUpScore
      evidenceCoverage
      matchMethod
      firstSeenAt
      lastSeenAt
      inactiveAt
      reviewedById
      reviewedAt
      reviewNote
      retryCount
      lastError
      hotel {
        id
        name
        refCode
        space {
          id
          name
        }
        businessAddress1
        businessZip
        businessCity
        businessCountry
      }
      events {
        id
        type
        createdAt
        userId
        detailsJson
      }
    }
  }
`;

export const MEETING_PACKAGE_RUNS_QUERY = gql`
  query MeetingPackageImportRuns($skip: Int, $take: Int) {
    meetingPackageImportRuns(skip: $skip, take: $take) {
      totalCount
      runs {
        id
        countries
        status
        startedAt
        finishedAt
        cancelRequestedAt
        totalVenueCount
        processedVenueCount
        discoveredCount
        apiVenueCount
        retryVenueCount
        prefilteredVenueCount
        excludedVenueCount
        updatedCount
        ignoredCount
        discoveredFailedCount
        accountingReconstructed
        linkedCount
        createdCount
        autoMatchEnabled
        autoCreateEnabled
        suggestedLinkedCount
        suggestedCreatedCount
        reviewCount
        inactiveCount
        failedCount
        error
      }
    }
  }
`;

export const MEETING_PACKAGE_IMPORT_ERRORS_QUERY = gql`
  query MeetingPackageImportErrors($runId: Int!, $skip: Int, $take: Int) {
    meetingPackageImportErrors(runId: $runId, skip: $skip, take: $take) {
      totalCount
      errors {
        id
        createdAt
        type
        error
        detailsJson
        venue {
          externalVenueId
          venueName
        }
      }
    }
  }
`;

export const MEETING_PACKAGE_HOTEL_CANDIDATES_QUERY = gql`
  query MeetingPackageHotelCandidates($search: String, $skip: Int, $take: Int) {
    meetingPackageHotelCandidates(search: $search, skip: $skip, take: $take) {
      totalCount
      hotels {
        id
        name
        refCode
        businessAddress1
        businessZip
        businessCity
        businessCountry
      }
    }
  }
`;

export const RUN_MEETING_PACKAGE_DISCOVERY = gql`
  mutation RunMeetingPackageDiscovery($countries: [String!]) {
    runMeetingPackageDiscovery(countries: $countries) {
      id
      status
      discoveredCount
      reviewCount
      failedCount
    }
  }
`;

export const CANCEL_MEETING_PACKAGE_DISCOVERY = gql`
  mutation CancelMeetingPackageDiscovery($runId: Int!) {
    cancelMeetingPackageDiscovery(runId: $runId)
  }
`;

export const MEETING_PACKAGE_API_CONFIG_QUERY = gql`
  query MeetingPackageApiConfig {
    meetingPackageApiConfig {
      configured
      apiKeyConfigured
      baseUrlConfigured
      baseUrlSource
    }
  }
`;

export const MEETING_PACKAGE_DRY_RUNS_QUERY = gql`
  query MeetingPackageMatchingDryRuns($skip: Int, $take: Int) {
    meetingPackageMatchingDryRunConfig {
      enabled
      defaultSampleSize
      defaultSeed
      sandboxEnabled
      sandboxConfigured
      defaultSandboxMaxVenues
    }
    meetingPackageMatchingDryRuns(skip: $skip, take: $take) {
      totalCount
      runs {
        id
        source
        status
        startedAt
        finishedAt
        sampleSize
        seed
        databaseHotelCount
        generatedCaseCount
        passRate
        incorrectAutoMatchCount
        reportJson
        error
      }
    }
  }
`;

export const RUN_MEETING_PACKAGE_MATCHING_DRY_RUN = gql`
  mutation RunMeetingPackageMatchingDryRun($source: MeetingPackageMatchingDryRunSource, $sampleSize: Int, $seed: Int) {
    runMeetingPackageMatchingDryRun(source: $source, sampleSize: $sampleSize, seed: $seed) {
      id
      source
      status
      startedAt
      finishedAt
      sampleSize
      seed
      databaseHotelCount
      generatedCaseCount
      passRate
      incorrectAutoMatchCount
      reportJson
      error
    }
  }
`;

export const DELETE_MEETING_PACKAGE_MATCHING_DRY_RUN = gql`
  mutation DeleteMeetingPackageMatchingDryRun($id: Int!) {
    deleteMeetingPackageMatchingDryRun(id: $id)
  }
`;

export const CLEAR_MEETING_PACKAGE_MATCHING_DRY_RUNS = gql`
  mutation ClearMeetingPackageMatchingDryRuns($source: MeetingPackageMatchingDryRunSource) {
    clearMeetingPackageMatchingDryRuns(source: $source)
  }
`;

export const REVIEW_MEETING_PACKAGE_MATCH = gql`
  mutation ReviewMeetingPackageMatch($externalVenueId: String!, $hotelId: Int!, $expectedPayloadHash: String!) {
    reviewMeetingPackageMatch(externalVenueId: $externalVenueId, hotelId: $hotelId, expectedPayloadHash: $expectedPayloadHash) {
      id
      status
      resolution
    }
  }
`;

export const APPROVE_MEETING_PACKAGE_CREATE = gql`
  mutation ApproveMeetingPackageCreate($externalVenueId: String!, $expectedPayloadHash: String!, $countryOverride: String) {
    approveMeetingPackageCreate(externalVenueId: $externalVenueId, expectedPayloadHash: $expectedPayloadHash, countryOverride: $countryOverride) {
      id
      status
      resolution
      hotelId
    }
  }
`;

export const UNLINK_MEETING_PACKAGE_MATCH = gql`
  mutation UnlinkMeetingPackageMatch($externalVenueId: String!) {
    unlinkMeetingPackageMatch(externalVenueId: $externalVenueId) {
      id
      status
      resolution
      hotelId
    }
  }
`;

export const RESET_MEETING_PACKAGE_CREATED_HOTEL = gql`
  mutation ResetMeetingPackageCreatedHotel($externalVenueId: String!) {
    resetMeetingPackageCreatedHotel(externalVenueId: $externalVenueId) {
      id
      status
      resolution
      hotelId
    }
  }
`;

export const IGNORE_MEETING_PACKAGE_VENUE = gql`
  mutation IgnoreMeetingPackageVenue($externalVenueId: String!, $note: String) {
    ignoreMeetingPackageVenue(externalVenueId: $externalVenueId, note: $note) {
      id
      status
      resolution
    }
  }
`;

export const REEVALUATE_MEETING_PACKAGE_VENUE = gql`
  mutation ReevaluateMeetingPackageVenue($externalVenueId: String!) {
    reevaluateMeetingPackageVenue(externalVenueId: $externalVenueId) {
      id
      status
      payloadHash
    }
  }
`;

export const RETRY_MEETING_PACKAGE_VENUE = gql`
  mutation RetryMeetingPackageVenue($externalVenueId: String!) {
    retryMeetingPackageVenue(externalVenueId: $externalVenueId) {
      id
      status
      retryCount
    }
  }
`;
